import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { 
  Search, 
  RefreshCw, 
  Users, 
  Clock,
  ArrowUpCircle,
  ArrowDownCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Monitor,
  Wifi,
  WifiOff,
  AlertTriangle,
  CheckCircle,
  Trash2,
  Settings,
  Eye,
  MoreVertical,
  Router,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format bytes to human readable
const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Format date time
const formatDateTime = (dateStr) => {
  if (!dateStr || dateStr === '-') return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('id-ID', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

// Mobile Card Component for each session
const SessionCard = ({ session, onDisconnect, getStatusBadge }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4 mb-3 shadow-sm">
      {/* Header - Always visible */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-sm font-semibold text-gray-900 dark:text-white truncate">
              {session.pppoe_username || '-'}
            </span>
            {getStatusBadge(session)}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
            {session.name || '-'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500">
            {session.account_number || ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {session.is_online && (
            <button
              onClick={() => onDisconnect(session)}
              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg dark:text-red-400"
              title="Disconnect"
            >
              <XCircle className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg dark:text-slate-400"
          >
            <MoreVertical className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Quick Info - Always visible */}
      <div className="mt-3 flex items-center justify-between gap-2 text-xs">
        <span className="inline-flex items-center justify-center min-w-[130px] max-w-full px-3 py-1 text-xs font-medium bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300 rounded-full whitespace-nowrap truncate">
          {session.service_plan_name || session.service_plan || session.package_name || '-'}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          <Clock className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-gray-600 dark:text-gray-400">{session.uptime || '-'}</span>
        </div>
      </div>
      
      {/* Expanded Details */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-500 mb-0.5">Router</p>
              <p className="text-gray-900 dark:text-white font-medium truncate">{session.router_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-500 mb-0.5">IP Address</p>
              <p className="font-mono text-gray-900 dark:text-white">{session.current_ip || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-500 mb-0.5">Owner</p>
              <p className="text-gray-900 dark:text-white">{session.owner_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-500 mb-0.5">MAC</p>
              <p className="font-mono text-xs text-gray-900 dark:text-white truncate">{session.current_mac || '-'}</p>
            </div>
          </div>
          
          {/* Traffic Stats */}
          <div className="flex items-center justify-around bg-gray-50 dark:bg-slate-900 rounded-lg py-3">
            <div className="text-center">
              <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                <ArrowUpCircle className="w-4 h-4" />
                <span className="text-sm font-medium">{session.upload_bytes ? formatBytes(session.upload_bytes) : '-'}</span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5 dark:text-slate-400">Upload</p>
            </div>
            <div className="w-px h-8 bg-gray-200 dark:bg-slate-700"></div>
            <div className="text-center">
              <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                <ArrowDownCircle className="w-4 h-4" />
                <span className="text-sm font-medium">{session.download_bytes ? formatBytes(session.download_bytes) : '-'}</span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5 dark:text-slate-400">Download</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PPPoEOnline = () => {
  const { token, user } = useAuth();
  const { selectedBranch, selectedRouter } = useApp();
  
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalSessions, setTotalSessions] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState('online');
  const [stats, setStats] = useState({ online: 0, offline: 0, isolir: 0, total: 0 });
  const [page, setPage] = useState(1);
  const [isCleanupDialogOpen, setIsCleanupDialogOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [staleSessions, setStaleSessions] = useState([]);
  const [staleLoading, setStaleLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupSettings, setCleanupSettings] = useState({
    auto_cleanup_enabled: true,
    stale_hours: 24
  });
  const [previewHours, setPreviewHours] = useState(24);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedPaket, setSelectedPaket] = useState('');
  const [paketList, setPaketList] = useState([]);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Toggle sort direction or set new sort field
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Extract comparable value for each sortable column
  const getSortValue = (session, field) => {
    switch (field) {
      case 'username':  return (session.pppoe_username || '').toLowerCase();
      case 'pelanggan': return (session.name || '').toLowerCase();
      case 'paket':     return (session.service_plan_name || session.service_plan || session.package_name || '').toLowerCase();
      case 'status':    return session.is_online ? 1 : 0;
      case 'router':    return (session.router_name || '').toLowerCase();
      case 'ip':        {
        // Parse IPv4 to numeric for natural sort, fallback to string
        const ip = session.current_ip || '';
        const m = ip.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)/);
        if (m) return (+m[1]<<24) + (+m[2]<<16) + (+m[3]<<8) + (+m[4]);
        return ip;
      }
      case 'uptime':    return Number(session.uptime_seconds || session.acctsessiontime || 0);
      case 'owner':     return (session.owner_name || '').toLowerCase();
      case 'upload':    return Number(session.upload_bytes || 0);
      case 'download':  return Number(session.download_bytes || 0);
      default:          return '';
    }
  };

  // Client-side sort to current page
  const displaySessions = React.useMemo(() => {
    if (!sortField) return sessions;
    const arr = [...sessions];
    arr.sort((a, b) => {
      const va = getSortValue(a, sortField);
      const vb = getSortValue(b, sortField);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [sessions, sortField, sortDir]);

  // Sortable TH component
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-indigo-600 dark:text-indigo-400" /> : <ArrowDown className="w-3 h-3 text-indigo-600 dark:text-indigo-400" />;
  };
  const SortableTh = ({ field, children }) => (
    <th
      onClick={() => toggleSort(field)}
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase cursor-pointer select-none hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
      data-testid={`th-sort-${field}`}
    >
      <span className="inline-flex items-center gap-1.5">
        {children}
        <SortIcon field={field} />
      </span>
    </th>
  );

  // Fetch stats with tenant isolation
  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      const params = new URLSearchParams();
      if (selectedBranch && selectedBranch !== 'all') {
        params.append('branch_id', selectedBranch);
      }
      if (selectedRouter && selectedRouter !== 'all') {
        params.append('router_id', selectedRouter);
      }
      if (user?.tenant_id) {
        params.append('tenant_id', user.tenant_id);
      }
      const queryStr = params.toString() ? '?' + params.toString() : '';
      const response = await axios.get(
        `${API_URL}/api/v2/pppoe-stats${queryStr}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token, selectedBranch, selectedRouter, user]);

  const fetchPaketList = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(
        `${API_URL}/api/v2/service-plans`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setPaketList(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching paket list:", error);
    }
  }, [token]);

  const fetchSessions = useCallback(async (showLoading = true) => {
    if (!token) return;
    
    if (showLoading) setLoading(true);
    else setRefreshing(true);
    
    try {
      const offset = (page - 1) * limit;
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        status: activeTab
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (selectedBranch && selectedBranch !== 'all') params.append('branch_id', selectedBranch);
      // 2026-05-28: respect global router selector from header
      if (selectedRouter && selectedRouter !== 'all') params.append('router_id', selectedRouter);
      if (user?.tenant_id) params.append('tenant_id', user.tenant_id);
      if (selectedPaket) params.append('service_plan', selectedPaket);
      if (user?.role?.toLowerCase() === 'owner' && user?.owner_id) params.append('owner_id', user.owner_id);
      
      const response = await axios.get(
        `${API_URL}/api/v2/pppoe-monitoring?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setSessions(response.data.data || []);
        setTotalSessions(response.data.total || 0);
        setTotalPages(Math.ceil((response.data.total || 0) / limit));
      }
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, page, limit, searchTerm, activeTab, selectedBranch, selectedRouter, selectedPaket, user]);

  const fetchCleanupSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v2/stale-sessions/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCleanupSettings(response.data.data);
        setPreviewHours(response.data.data.stale_hours || 24);
      }
    } catch (error) {
      console.error('Error fetching cleanup settings:', error);
    }
  }, [token]);

  const previewStaleSessions = async () => {
    setStaleLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/v2/stale-sessions/preview?hours=${previewHours}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setStaleSessions(response.data.data || []);
      }
    } catch (error) {
      console.error('Error previewing stale sessions:', error);
      toast.error('Gagal memuat preview');
    } finally {
      setStaleLoading(false);
    }
  };

  const cleanupStaleSessions = async () => {
    if (!window.confirm(`Yakin ingin membersihkan ${staleSessions.length} session stale?`)) return;
    
    setCleanupLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/v2/stale-sessions/cleanup?hours=${previewHours}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
        setStaleSessions([]);
        setIsCleanupDialogOpen(false);
        fetchSessions();
        fetchStats();
      }
    } catch (error) {
      console.error('Error cleaning stale sessions:', error);
      toast.error('Gagal membersihkan session');
    } finally {
      setCleanupLoading(false);
    }
  };

  const syncAndCleanup = async () => {
    setCleanupLoading(true);
    try {
      toast.info('Sync status dari MikroTik...');
      const syncResponse = await axios.post(`${API_URL}/api/v2/pppoe-monitoring/sync-online-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (syncResponse.data.success) {
        toast.success(syncResponse.data.message);
      }
      
      toast.info('Membersihkan session stale...');
      const cleanupResponse = await axios.post(`${API_URL}/api/v2/stale-sessions/cleanup?hours=24`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (cleanupResponse.data.success) {
        toast.success(cleanupResponse.data.message);
      }
      
      fetchSessions();
      fetchStats();
      
    } catch (error) {
      console.error('Error in sync & cleanup:', error);
      toast.error('Gagal melakukan sync & cleanup');
    } finally {
      setCleanupLoading(false);
    }
  };

  const saveCleanupSettings = async () => {
    try {
      const response = await axios.put(`${API_URL}/api/v2/stale-sessions/settings`, cleanupSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success('Settings berhasil disimpan');
        setIsSettingsDialogOpen(false);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Gagal menyimpan settings');
    }
  };

  useEffect(() => {
    fetchSessions();
    fetchStats();
    fetchCleanupSettings();
    fetchPaketList();
    
    const interval = setInterval(() => {
      fetchSessions(false);
      fetchStats();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchSessions, fetchStats, fetchCleanupSettings, fetchPaketList]);

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  useEffect(() => {
    setPage(1);
    fetchSessions();
    fetchStats();
  }, [selectedBranch, selectedRouter]);

  const handleRefresh = () => {
    fetchSessions(false);
    fetchStats();
    toast.success('Data diperbarui');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchSessions();
  };

  const handleDisconnect = async (session) => {
    if (!window.confirm(`Disconnect user ${session.pppoe_username}?`)) return;
    
    try {
      await axios.post(
        `${API_URL}/api/v2/pppoe/disconnect/${session.pppoe_username}`,
        { nas_ip: session.nas_ip },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User ${session.pppoe_username} disconnected`);
      fetchSessions(false);
      fetchStats();
    } catch (error) {
      toast.error('Gagal disconnect user');
    }
  };

  const tabs = [
    { id: 'online', label: 'Online', icon: Wifi, count: stats.online, color: 'text-green-600 bg-green-50 border-green-500 dark:bg-green-900/30' },
    { id: 'offline', label: 'Offline', icon: WifiOff, count: stats.offline, color: 'text-gray-600 bg-gray-50 border-gray-500 dark:bg-gray-800' },
    { id: 'isolir', label: 'Isolir', icon: AlertTriangle, count: stats.isolir, color: 'text-red-600 bg-red-50 border-red-500 dark:bg-red-900/30' },
    { id: 'all', label: 'Semua', icon: Users, count: stats.total, color: 'text-blue-600 bg-blue-50 border-blue-500 dark:bg-blue-900/30' },
  ];

  const getStatusBadge = (session) => {
    if (session.is_isolated) {
      return (
        <span className="inline-flex items-center justify-center gap-1 min-w-[90px] px-3 py-1 text-xs font-medium rounded-full border border-red-500 bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
          Isolir
        </span>
      );
    }
    if (session.is_online) {
      return (
        <span className="inline-flex items-center justify-center gap-1 min-w-[90px] px-3 py-1 text-xs font-medium rounded-full border border-green-500 bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400">
          <Wifi className="w-3 h-3" />
          Online
        </span>
      );
    }
    return (
      <span className="inline-flex items-center justify-center gap-1 min-w-[90px] px-3 py-1 text-xs font-medium rounded-full border border-gray-400 bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
        <WifiOff className="w-3 h-3" />
        Offline
      </span>
    );
  };


  return (
    <div className="p-3 sm:p-4 lg:p-6 space-y-4 min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Monitor className="w-6 h-6 sm:w-7 sm:h-7 text-violet-600 dark:text-violet-400" />
              <span className="hidden sm:inline">PPPoE Monitoring</span>
              <span className="sm:hidden">PPPoE</span>
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {lastUpdated && `Update: ${formatDateTime(lastUpdated)}`}
            </p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={syncAndCleanup}
            disabled={cleanupLoading}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {cleanupLoading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">Sync & Cleanup</span>
            <span className="sm:hidden">Sync</span>
          </button>
          <button
            onClick={() => setIsSettingsDialogOpen(true)}
            className="p-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 bg-violet-600 text-white text-sm rounded-lg hover:bg-violet-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats Tabs - Responsive Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 transition-all ${
              activeTab === tab.id 
                ? tab.color + ' border-current font-semibold' 
                : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-slate-700 hover:border-gray-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="text-sm">{tab.label}</span>
            <span className={`px-1.5 py-0.5 text-xs rounded-full ${
              activeTab === tab.id ? 'bg-white/50 dark:bg-black/20' : 'bg-gray-100 dark:bg-slate-700'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-2">
        <select
          value={selectedPaket}
          onChange={(e) => {
            setSelectedPaket(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 border dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 bg-white dark:bg-slate-700 dark:text-white text-sm"
        >
          <option value="">Semua Paket</option>
          {paketList.map((paket) => (
            <option key={paket.id} value={paket.name}>
              {paket.name}
            </option>
          ))}
        </select>

        <form onSubmit={handleSearch} className="flex gap-2 flex-1">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cari username, nama, IP..."
              className="w-full pl-10 pr-4 py-2 border dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 bg-white dark:bg-slate-700 dark:text-white text-sm"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 text-sm"
          >
            Cari
          </button>
        </form>
      </div>

      {/* Content - Desktop Table / Mobile Cards */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden">
        {/* Desktop Table - Hidden on mobile */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
            <thead className="bg-gray-50 dark:bg-slate-700">
              <tr>
                <SortableTh field="username">Username</SortableTh>
                <SortableTh field="pelanggan">Pelanggan</SortableTh>
                <SortableTh field="paket">Paket</SortableTh>
                <SortableTh field="status">Status</SortableTh>
                <SortableTh field="router">Router</SortableTh>
                <SortableTh field="ip">IP Address</SortableTh>
                <SortableTh field="uptime">Uptime</SortableTh>
                <SortableTh field="owner">Owner</SortableTh>
                <SortableTh field="upload">Upload</SortableTh>
                <SortableTh field="download">Download</SortableTh>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Aksi</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="11" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Memuat data...
                  </td>
                </tr>
              ) : sessions.length === 0 ? (
                <tr>
                  <td colSpan="11" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    Tidak ada data
                  </td>
                </tr>
              ) : (
                displaySessions.map((session, idx) => (
                  <tr key={session.id || idx} className="hover:bg-gray-50 dark:hover:bg-slate-700">
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm text-gray-900 dark:text-white">{session.pppoe_username || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{session.name || '-'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{session.account_number || ''}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center justify-center min-w-[150px] px-3 py-1 text-xs font-medium bg-violet-100 dark:bg-violet-900 text-violet-700 dark:text-violet-300 rounded-full whitespace-nowrap">{session.service_plan_name || session.service_plan || session.package_name || '-'}</span>
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(session)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{session.router_name || '-'}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm text-gray-900 dark:text-white">{session.current_ip || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm flex items-center gap-1">
                        <Clock className="w-3 h-3 text-gray-400" />
                        {session.uptime || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{session.owner_name || '-'}</td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-green-600 flex items-center gap-1 dark:text-green-400">
                        <ArrowUpCircle className="w-3 h-3" />
                        {session.upload_bytes ? formatBytes(session.upload_bytes) : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-blue-600 flex items-center gap-1 dark:text-blue-400">
                        <ArrowDownCircle className="w-3 h-3" />
                        {session.download_bytes ? formatBytes(session.download_bytes) : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {session.is_online && (
                        <button
                          onClick={() => handleDisconnect(session)}
                          className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded dark:text-red-400"
                          title="Disconnect"
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards - Shown only on mobile/tablet */}
        <div className="lg:hidden p-3">
          {loading ? (
            <div className="text-center py-8 text-gray-500 dark:text-slate-400">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Memuat data...
            </div>
          ) : displaySessions.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-slate-400">
              <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              Tidak ada data
            </div>
          ) : (
            displaySessions.map((session, idx) => (
              <SessionCard
                key={session.id || idx}
                session={session}
                onDisconnect={handleDisconnect}
                getStatusBadge={getStatusBadge}
              />
            ))
          )}
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-700">
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
              <span className="hidden sm:inline">Menampilkan </span>
              {((page - 1) * limit) + 1} - {Math.min(page * limit, totalSessions)} dari {totalSessions}
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-50 dark:text-white"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs sm:text-sm px-2">{page}/{totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-50 dark:text-white"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Settings Dialog */}
      <Dialog open={isSettingsDialogOpen} onOpenChange={setIsSettingsDialogOpen}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-600 dark:text-slate-300" />
              Pengaturan Auto Cleanup
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700">
              <input
                type="checkbox"
                checked={cleanupSettings.auto_cleanup_enabled}
                onChange={(e) => setCleanupSettings({...cleanupSettings, auto_cleanup_enabled: e.target.checked})}
                className="w-4 h-4 text-violet-600 rounded dark:text-violet-400"
              />
              <div>
                <p className="font-medium text-sm">Auto Cleanup Aktif</p>
                <p className="text-xs text-gray-500 dark:text-slate-400">Otomatis membersihkan session stale</p>
              </div>
            </label>
            
            <div>
              <label className="block text-sm font-medium mb-1">Batas Waktu Session Stale</label>
              <select
                value={cleanupSettings.stale_hours}
                onChange={(e) => setCleanupSettings({...cleanupSettings, stale_hours: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              >
                <option value="6">6 jam</option>
                <option value="12">12 jam</option>
                <option value="24">24 jam (rekomendasi)</option>
                <option value="48">48 jam</option>
                <option value="72">72 jam</option>
              </select>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <button
                onClick={() => setIsSettingsDialogOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm dark:text-slate-200 dark:bg-slate-800"
              >
                Batal
              </button>
              <button
                onClick={saveCleanupSettings}
                className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm"
              >
                Simpan
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PPPoEOnline;
